import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Box, Card, CardContent, Chip, CircularProgress, Stack, Typography } from '@mui/joy';
import { Link } from 'react-router-dom';
import { useProfile } from '../hooks/useProfile';
import { listAssignedJobs } from './project-workspace/jobCard.service';
import { groupJobsByInstallDate, jobInstallDays, RECENT_JOBS_KEY } from './project-workspace/fitterJobList';
import type { AssignedJob } from './project-workspace/fitterJobList';
import {
  formatShortDay,
  installSpanNote,
  toInstallTimeOnly,
  wholeJobNote,
} from './project-workspace/jobCardInstallDate';
import { welcomeMessage } from './project-workspace/welcomeName';
import {
  fetchChichesterForecast,
  fetchJobSiteForecasts,
  formatForecastLine,
  jobWeatherTargetsForDay,
  londonWeatherDayKey,
  type SiteForecast,
} from './project-workspace/chichesterWeather';

type ViewAs = { id: string; full_name: string | null; email: string | null };

type FitterJobsPageProps = {
  /** Office preview: show this crew member's dashboard instead of the signed-in user's. */
  viewAs?: ViewAs;
  /** Where a job opens; the preview sends the office to the workspace instead of the crew card page. */
  jobHref?: (job: AssignedJob) => string;
};

const FitterJobsPage: React.FC<FitterJobsPageProps> = ({ viewAs, jobHref }) => {
  const { profile: signedIn } = useProfile();
  const profile = viewAs || signedIn;
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [rows, setRows] = useState<AssignedJob[]>([]);
  const [forecasts, setForecasts] = useState<SiteForecast[]>([]);
  const groups = useMemo(() => groupJobsByInstallDate(rows), [rows]);
  const greeting = welcomeMessage(profile?.full_name, profile?.email);
  const todayKey = londonWeatherDayKey();
  const todaysJobs = useMemo(
    () => rows.filter((row) => jobInstallDays(row).includes(todayKey)),
    [rows, todayKey]
  );

  useEffect(() => {
    if (!profile?.id) return;
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const jobs = await listAssignedJobs(profile.id);
        if (!cancelled) setRows(jobs);
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load jobs');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    void load();
    return () => { cancelled = true; };
  }, [profile?.id]);

  useEffect(() => {
    let cancelled = false;
    const loadWeather = async () => {
      const office = await fetchChichesterForecast().catch(() => null);
      const sites = await fetchJobSiteForecasts(jobWeatherTargetsForDay(todaysJobs)).catch(() => []);
      if (cancelled) return;
      const next = [...sites];
      if (office && !next.some((row) => row.label.startsWith('Chichester'))) {
        next.unshift(office);
      }
      setForecasts(next);
    };
    void loadWeather();
    return () => { cancelled = true; };
  }, [todaysJobs]);

  return (
    <Box
      sx={{
        px: { xs: 0.5, sm: 1 },
        py: { xs: 1, md: 2 },
        maxWidth: 720,
        mx: 'auto',
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        minHeight: { xs: 'calc(100dvh - var(--mobile-header-height, 80px) - 24px)', md: 'auto' },
      }}
    >
      <Box sx={{ mb: 2.5 }}>
        <Typography level="h3" sx={{ mb: 0.35, fontSize: { xs: '1.45rem', sm: '1.75rem' } }}>
          {greeting}
        </Typography>
        <Typography level="body-sm" sx={{ color: 'neutral.500', fontWeight: 700 }}>
          Jobs assigned to you.
        </Typography>
      </Box>
      {loading && <CircularProgress size="sm" />}
      {error && <Alert color="danger">{error}</Alert>}
      {!loading && !error && rows.length === 0 && (
        <Typography level="body-sm" sx={{ color: 'neutral.500' }}>No jobs assigned yet.</Typography>
      )}
      <Stack gap={2.5} sx={{ flex: 1 }}>
        {groups.map((group) => (
          <Box key={group.key}>
            <Typography
              level="title-sm"
              sx={{
                color: 'neutral.600',
                letterSpacing: '0.02em',
                textTransform: 'uppercase',
                fontSize: '0.78rem',
                mb: 1,
                px: 0.25,
                position: 'sticky',
                top: 0,
                bgcolor: 'var(--jarvis-bg-body)',
                py: 0.75,
                zIndex: 1,
              }}
            >
              {group.title}
            </Typography>
            <Stack gap={1.25}>
              {group.items.map((row) => {
                const days = jobInstallDays(row);
                const recent = group.key === RECENT_JOBS_KEY;
                const startTime = recent ? null : toInstallTimeOnly(row.install_date);
                // The finish time belongs to the last on-site day
                const lastDay = days[days.length - 1];
                const endTime = startTime && row.install_end_time && (days.length < 2 || group.key === lastDay)
                  ? row.install_end_time
                  : null;
                let time = startTime;
                if (endTime) time = days.length < 2 ? `${startTime}–${endTime}` : `until ${endTime}`;
                const spanNote = recent
                  ? `Last on site ${formatShortDay(days[days.length - 1])}`
                  : installSpanNote(days, group.key);
                const jobNote = wholeJobNote(days, row.job_days || []);
                return (
                  <Card
                    key={row.job_card_id}
                    variant="outlined"
                    component={Link}
                    to={jobHref ? jobHref(row) : `/job-records/${row.job_card_id}`}
                    sx={{
                      textDecoration: 'none',
                      color: 'inherit',
                      minHeight: 72,
                      '&:hover': { borderColor: 'primary.outlinedBorder' },
                      '&:active': { bgcolor: 'neutral.softBg' },
                    }}
                  >
                    <CardContent sx={{ py: { xs: 1.5, sm: 2 }, px: { xs: 1.5, sm: 2 } }}>
                      <Stack direction="row" justifyContent="space-between" alignItems="baseline" gap={1}>
                        <Typography level="title-md" sx={{ wordBreak: 'break-word' }}>
                          {row.job_title || 'Job card'}
                        </Typography>
                        {time && (
                          <Typography level="title-sm" sx={{ color: 'neutral.600', flexShrink: 0 }}>
                            {time}
                          </Typography>
                        )}
                      </Stack>
                      <Typography level="body-sm">{row.project_name || 'Untitled workspace'}</Typography>
                      <Typography level="body-sm">{row.customer_name || 'No customer'}</Typography>
                      {!recent && days.length > 1 ? (
                        <Box sx={{ mt: 1 }}>
                          <Typography level="body-xs" sx={{ color: 'primary.plainColor', fontWeight: 700, mb: 0.5 }}>
                            {days.length}-day job{days.includes(group.key) ? ` · day ${days.indexOf(group.key) + 1} of ${days.length}` : ''}
                          </Typography>
                          <Stack direction="row" gap={0.5} flexWrap="wrap">
                            {days.map((day) => (
                              <Chip
                                key={day}
                                size="sm"
                                variant={day === group.key ? 'solid' : 'soft'}
                                color="primary"
                                sx={{ fontWeight: 600 }}
                              >
                                {formatShortDay(day)}
                              </Chip>
                            ))}
                          </Stack>
                        </Box>
                      ) : spanNote && (
                        <Typography
                          level="body-xs"
                          sx={{ color: recent ? 'neutral.500' : 'primary.plainColor', fontWeight: 600, mt: 0.25 }}
                        >
                          {spanNote}
                        </Typography>
                      )}
                      {jobNote && (
                        <Typography level="body-xs" sx={{ color: 'neutral.500' }}>{jobNote}</Typography>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </Stack>
          </Box>
        ))}
      </Stack>
      {forecasts.length > 0 && (
        <Box sx={{ mt: 3, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
          <Typography
            level="title-sm"
            sx={{
              color: 'neutral.600',
              letterSpacing: '0.02em',
              textTransform: 'uppercase',
              fontSize: '0.78rem',
              mb: 1,
              px: 0.25,
            }}
          >
            Weather today
          </Typography>
          <Stack gap={0.5}>
            {forecasts.map((forecast) => (
              <Typography key={`${forecast.label}-${forecast.postcode || 'office'}`} level="body-sm" sx={{ color: 'neutral.700' }}>
                {forecast.icon} {formatForecastLine(forecast)}
              </Typography>
            ))}
          </Stack>
        </Box>
      )}
    </Box>
  );
};

export default FitterJobsPage;
